<template>
    <div class="print-page">
        <div class="top-bar">
            <button class="btn btn-secondary logout-btn" @click="logout">
                Logout →
            </button>
        </div>

        <div class="header-section">
            <img src="/images/logo.png" alt="Logo" class="page-logo" />

            <h1 v-if="isMasterDashboard" class="project-title">Imagens de Projetos</h1>
            <h1 v-else class="project-title">Projeto: <span>{{ projectTitle }}</span></h1>
        </div>

        <div v-if="isMasterDashboard" class="dashboard-panel">
            <h2>Selecione um projeto para visualizar:</h2>
            <div class="projects-list-grid">
                <button v-for="project in projects" :key="project._id" class="btn btn-outline project-select-btn"
                    @click="$router.push(`/${project.projectCode}`)">
                    📁 {{ project.projectCode }}
                </button>
            </div>
        </div>

        <div v-else>
            <div v-if="isLoading" class="spinner-container">
                <div class="loading-spinner"></div>
                <p>Carregando mídias...</p>
            </div>

            <div v-else class="content-wrapper">
                <div class="controls-panel">
                    <div class="instruction">
                        <h2>Selecione o tipo de mídia que deseja imprimir:</h2>
                        <p class="subtitle">Clique em qualquer item para abrir a mídia em tamanho real.</p>
                    </div>
                    <button class="btn btn-primary download-btn" :disabled="!filteredMedia.length || isDownloading"
                        @click="downloadAll">
                        <span v-if="isDownloading" class="btn-spinner"></span>
                        <span v-else>⬇</span>
                        {{ isDownloading ? "Preparando arquivo ZIP..." : "Baixar todas as mídias desta página" }}
                    </button>
                </div>

                <div v-if="filteredMedia.length" class="media-grid">
                    <div v-for="media in filteredMedia" :key="media._id" class="media-card"
                        @click="openMediaInNewTab(media.data, media)">
                        <div class="preview-container">
                            <video v-if="isVideo(media)" :src="media.data" preload="metadata"
                                class="media-preview"></video>
                            <img v-else :src="media.data" :alt="media.mediaName" class="media-preview" />
                            <span class="type-badge" :class="isVideo(media) ? 'badge-video' : 'badge-image'">
                                {{ isVideo(media) ? 'Vídeo' : 'Imagem' }}
                            </span>
                        </div>
                        <div class="media-info">
                            <p class="media-name" :title="media.mediaName">{{ media.mediaName }}</p>
                        </div>
                    </div>
                </div>

                <div v-else class="empty-state">
                    <p>Nenhuma mídia encontrada para este projeto.</p>
                </div>

                <div class="pagination-panel">
                    <button class="btn btn-outline" :disabled="currentPage === 1" @click="changePage(-1)">←
                        Anterior</button>
                    <span class="page-indicator">Página <strong>{{ currentPage }}</strong></span>
                    <button class="btn btn-outline" :disabled="!hasMoreMedia" @click="changePage(1)">Próxima →</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import JSZip from "jszip";
import { saveAs } from "file-saver";

export default {
    name: "ProjectPrintPage",
    data() {
        return {
            projects: [], // Array for storing master project choices
            media: [],
            currentPage: 1,
            filterText: "",
            hasMoreMedia: true,
            isLoading: false,
            isDownloading: false,
        };
    },
    computed: {
        // Core Logic: Checks if the user path is exactly the master print landing route
        isMasterDashboard() {
            return this.$route.path === "/print";
        },
        selectedProject() {
            if (this.isMasterDashboard) return null;
            if (this.$route.params.projectCode) {
                return this.$route.params.projectCode;
            }
            return this.$route.path.replace(/^\/+|\/+$/g, '') || null;
        },
        projectTitle() {
            if (!this.selectedProject) return "";
            return this.selectedProject.charAt(0).toUpperCase() + this.selectedProject.slice(1);
        },
        filteredMedia() {
            const filter = this.filterText.toLowerCase();
            return this.media.filter((media) =>
                media.mediaName && media.mediaName.toLowerCase().includes(filter)
            );
        },
    },
    watch: {
        // Runs immediately upon component entry or route transformations
        selectedProject: {
            immediate: true,
            handler(newProject) {
                if (newProject) {
                    this.fetchMedia(1);
                } else if (this.isMasterDashboard) {
                    this.fetchProjects();
                }
            }
        }
    },
    methods: {
        logout() {
            localStorage.removeItem("authRoles");
            this.$router.push("/");
        },
        async fetchProjects() {
            this.isLoading = true;
            try {
                const response = await fetch("/api/projects/activeProjects");
                this.projects = await response.json();
            } catch (error) {
                console.error("Error loading index directory list:", error);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchMedia(page = 1) {
            if (!this.selectedProject) return;
            this.isLoading = true;
            try {
                this.currentPage = page;
                const response = await fetch(
                    `/api/projects/${this.selectedProject}/media?page=${this.currentPage}`
                );
                const data = await response.json();
                this.media = data.media || [];
                this.hasMoreMedia = data.hasNextPage;
            } catch (error) {
                console.error("Error fetching media grid targets:", error);
            } finally {
                this.isLoading = false;
            }
        },
        changePage(offset) {
            const newPage = this.currentPage + offset;
            this.fetchMedia(newPage);
        },
        isVideo(media) {
            if (!media) return false;
            if (media.mediaType && media.mediaType.startsWith('video/')) {
                return true;
            }
            const targetString = (media.data || media.mediaName || '').toLowerCase();
            const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
            return videoExtensions.some(ext => targetString.includes(ext));
        },
        openMediaInNewTab(mediaData, media) {
            const newTab = window.open();
            if (newTab) {
                newTab.document.body.style.margin = "0";
                newTab.document.body.style.backgroundColor = "#0f172a";
                newTab.document.body.style.display = "flex";
                newTab.document.body.style.justifyContent = "center";
                newTab.document.body.style.alignItems = "center";
                newTab.document.body.style.height = "100vh";

                let mediaElement = this.isVideo(media)
                    ? newTab.document.createElement("video")
                    : newTab.document.createElement("img");

                mediaElement.src = mediaData;
                if (this.isVideo(media)) {
                    mediaElement.controls = true;
                    mediaElement.autoplay = true;
                }

                mediaElement.style.maxWidth = "95vw";
                mediaElement.style.maxHeight = "95vh";
                mediaElement.style.borderRadius = "8px";
                newTab.document.body.appendChild(mediaElement);
            }
        },
        async downloadAll() {
            if (!this.filteredMedia.length) return;
            this.isDownloading = true;
            try {
                const zip = new JSZip();
                const folderName = this.selectedProject || "media";
                const folder = zip.folder(folderName);

                const tasks = this.filteredMedia.map(async (media, index) => {
                    try {
                        const res = await fetch(media.data);
                        const blob = await res.blob();
                        const mime = blob.type || "image/png";
                        const ext = (mime.split("/")[1] || "png").split("+")[0].toLowerCase();
                        const safeName = (media.mediaName || `media_${index + 1}`).replace(/[^\w\-]+/g, "_");
                        folder.file(`${safeName}.${ext}`, blob);
                    } catch (err) {
                        console.error("Error processing archive element:", err);
                    }
                });

                await Promise.all(tasks);
                const zipBlob = await zip.generateAsync({ type: "blob" });
                saveAs(zipBlob, `${folderName}_page_${this.currentPage}.zip`);
            } catch (error) {
                console.error("Error downloading collection ZIP file:", error);
            } finally {
                this.isDownloading = false;
            }
        },
    }
};
</script>

<style scoped>
/* Unified Stylesystem Rules Variables Base */
.print-page {
    --primary-color: #3b82f6;
    --primary-hover: #1d4ed8;
    --bg-main: #f8fafc;
    --card-bg: #ffffff;
    --text-dark: #0f172a;
    --text-muted: #64748b;
    --border-light: #e2e8f0;

    background-color: var(--bg-main);
    min-height: 100vh;
    padding: 24px;
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-dark);
}

.top-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
}

.header-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 32px;
    text-align: center;
}

.page-logo {
    width: 280px;
    max-width: 100%;
    height: auto;
    margin-bottom: 20px;
}

.project-title {
    font-size: 2.25rem;
    font-weight: 800;
    color: #1e293b;
}

.project-title span {
    color: var(--primary-color);
}

/* Custom Styling for the Master Selection Dashboard Layout Grid */
.dashboard-panel {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 32px;
    border: 1px solid var(--border-light);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    max-width: 800px;
    margin: 0 auto;
}

.dashboard-panel h2 {
    font-size: 1.25rem;
    color: var(--text-muted);
    margin-bottom: 24px;
}

.projects-list-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
}

.project-select-btn {
    padding: 16px !important;
    font-size: 1.05rem !important;
    text-align: left;
    justify-content: flex-start;
    border-width: 2px;
}

.project-select-btn:hover {
    border-color: var(--primary-color) !important;
    color: var(--primary-color) !important;
    transform: scale(1.02);
}

/* Base Controls/Grid Blocks Styles definitions from earlier */
.controls-panel {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 20px 24px;
    border: 1px solid var(--border-light);
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
}

@media (min-width: 768px) {
    .controls-panel {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
}

.instruction h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0 0 4px 0;
}

.subtitle {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-primary {
    background-color: var(--primary-color);
    color: white;
}

.btn-primary:not(:disabled):hover {
    background-color: var(--primary-hover);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.btn-secondary {
    background-color: #ef4444;
    color: white;
}

.btn-outline {
    background-color: var(--card-bg);
    border-color: var(--border-light);
    color: #475569;
}

.btn-outline:not(:disabled):hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
}

.media-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px;
    margin-bottom: 32px;
}

.media-card {
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px solid var(--border-light);
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    display: flex;
    flex-direction: column;
}

.media-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.1);
}

.preview-container {
    position: relative;
    width: 100%;
    padding-top: 100%;
    background: #0f172a;
}

.media-preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.type-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 20px;
    color: white;
}

.badge-video {
    background-color: #f59e0b;
}

.badge-image {
    background-color: #10b981;
}

.media-info {
    padding: 12px;
    background: var(--card-bg);
    flex-grow: 1;
    display: flex;
    align-items: center;
}

.media-name {
    font-size: 0.813rem;
    font-weight: 500;
    color: #334155;
    margin: 0;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pagination-panel {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 12px 24px;
    border: 1px solid var(--border-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 450px;
    margin: 0 auto;
}

.page-indicator {
    font-size: 0.875rem;
    color: var(--text-muted);
}

.spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 0;
    color: var(--text-muted);
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-light);
    border-top-color: var(--primary-color);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 12px;
}

.btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top-color: currentColor;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

.empty-state {
    text-align: center;
    padding: 64px 16px;
    color: var(--text-muted);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>